import React, { FC, useState, useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { hasPermission } from '@relif/zeller-roles';
import { PrimaryBtn } from 'components/UI/Buttons';
import useCurrentUser from 'hooks/useCurrentUser';
import { useGetUserBusinessUnitsQuery } from 'services/api/businessUnits.api';
import { useCreateUserMutation, useUpdateUserMutation } from 'services/api/user.api';
import { User, UserAttributes } from 'services/types/user';

interface Props {
  open: boolean;
  onClose: () => void;
  user?: User | null;
  onSuccess: () => void;
}

const getRoleOptions = (currentUser: User) => {
  const ROLE_OPTIONS = [
    { value: 'seller', label: 'Vendedor' },
    { value: 'sellerWithAssignment', label: 'Vendedor con Asignación' },
    { value: 'branchManager', label: 'Gerente de Sucursal' },
    { value: 'businessUnitAdmin', label: 'Administrador de Unidad de Negocio' },
  ];
  if (currentUser.isAdmin || currentUser.role === 'admin' || currentUser.role === 'businessUnitAdmin') {
    return ROLE_OPTIONS;
  }
  if (currentUser.role === 'branchManager') {
    return ROLE_OPTIONS.filter((role) => role.value !== 'businessUnitAdmin');
  }
  return [];
};

const UserFormModal: FC<Props> = ({ open, onClose, user, onSuccess }) => {
  const currentUser = useCurrentUser();
  const isEditMode = !!user;

  const [formData, setFormData] = useState<UserAttributes>({
    email: '',
    businessUnitIds: [],
    name: '',
    brandsToSell: [],
    branchOffice: '',
    role: 'seller',
  });

  const [sendEmail, setSendEmail] = useState(true);
  const [errors, setErrors] = useState<Partial<Record<keyof UserAttributes, string>>>({});
  const [newBrand, setNewBrand] = useState('');

  const { data: businessUnits } = useGetUserBusinessUnitsQuery(null);
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        businessUnitIds: user.businessUnitIds || [],
        name: user.name || '',
        brandsToSell: user.brandsToSell || [],
        branchOffice: user.branchOffice || '',
        role: user.role,
      });
    } else {
      setFormData({
        email: '',
        businessUnitIds: [],
        name: '',
        brandsToSell: [],
        branchOffice: '',
        role: 'seller',
      });
    }
    setErrors({});
  }, [user]);

  const handleInputChange = (field: keyof UserAttributes, value: string | string[] | number[] | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserAttributes, string>> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.name?.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.businessUnitIds.length) {
      newErrors.businessUnitIds = 'Debe seleccionar al menos una unidad de negocio';
    } else {
      const companyIds = businessUnits
        ?.filter((bu) => formData.businessUnitIds?.includes(bu.id))
        .map((bu) => bu.company?.id);
      if (new Set(companyIds).size !== 1) {
        newErrors.businessUnitIds = 'No se pueden seleccionar unidades de negocio de diferentes empresas';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    if (isEditMode && user) {
      updateUser({
        userId: user.id,
        userAttributes: formData,
      })
        .then(() => {
          onSuccess();
          onClose();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[UserFormModal] Error updating user:', error);
        });
    } else {
      createUser({
        userAttributes: formData,
        sendEmail,
      })
        .then(() => {
          onSuccess();
          onClose();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[UserFormModal] Error creating user:', error);
        });
    }
  };

  const handleAddBrand = () => {
    if (newBrand.trim() && !formData.brandsToSell?.includes(newBrand.trim())) {
      const updatedBrands = [...(formData.brandsToSell || []), newBrand.trim().toLocaleUpperCase()];
      handleInputChange('brandsToSell', updatedBrands);
      setNewBrand('');
    }
  };

  const handleRemoveBrand = (brandToRemove: string) => {
    const updatedBrands = (formData.brandsToSell || []).filter((brand) => brand !== brandToRemove);
    handleInputChange('brandsToSell', updatedBrands);
  };

  const handleBrandKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddBrand();
    }
  };

  const canCreate = !isEditMode && currentUser && hasPermission(currentUser, 'users', 'create', formData);
  const canUpdate = isEditMode && user && currentUser && hasPermission(currentUser, 'users', 'update', user);
  const canSubmit = isEditMode ? canUpdate : canCreate;

  if (!currentUser) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <Stack spacing={3} p={3}>
          <Typography variant="h5">{isEditMode ? 'Editar Usuario' : 'Crear Usuario'}</Typography>

          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            required
            fullWidth
          />

          <TextField
            label="Nombre"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />

          <FormControl fullWidth error={!!errors.businessUnitIds}>
            <InputLabel>Unidades de Negocio</InputLabel>
            <Select
              multiple
              value={formData.businessUnitIds.map(String)}
              onChange={(e) => handleInputChange('businessUnitIds', (e.target.value as string[]).map(Number))}
              input={<OutlinedInput label="Unidades de Negocio" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const businessUnit = businessUnits?.find((bu) => bu.id === Number(value));
                    return <Chip key={value} label={businessUnit?.name || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {businessUnits?.map((bu) => (
                <MenuItem key={bu.id} value={bu.id.toString()}>
                  {bu.name}
                </MenuItem>
              ))}
            </Select>
            {errors.businessUnitIds && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                {errors.businessUnitIds}
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select value={formData.role} onChange={(e) => handleInputChange('role', e.target.value)} label="Rol">
              {getRoleOptions(currentUser).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Sucursal"
            value={formData.branchOffice}
            onChange={(e) => handleInputChange('branchOffice', e.target.value)}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Marcas que Vende
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
                onKeyPress={handleBrandKeyPress}
                placeholder="Agregar marca..."
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <IconButton
                onClick={handleAddBrand}
                disabled={!newBrand.trim() || formData.brandsToSell?.includes(newBrand.trim())}
                size="small"
              >
                <AddIcon />
              </IconButton>
            </Box>
            {formData.brandsToSell && formData.brandsToSell.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.brandsToSell.map((brand) => (
                  <Chip
                    key={brand}
                    label={brand}
                    onDelete={() => handleRemoveBrand(brand)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>

          {!isEditMode && (
            <Box>
              {currentUser.isAdmin ? (
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Checkbox
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      inputProps={{ 'aria-label': 'send email' }}
                    />
                  }
                  label="Enviar email de bienvenida"
                  labelPlacement="start"
                />
              ) : (
                <Typography variant="body1">Se enviará un email de bienvenida con la contraseña generada</Typography>
              )}
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <PrimaryBtn variant="outlined" onClick={onClose}>
              Cancelar
            </PrimaryBtn>
            <PrimaryBtn onClick={handleSubmit} disabled={!canSubmit || isCreating || isUpdating}>
              {isEditMode ? 'Actualizar' : 'Crear'}
            </PrimaryBtn>
          </Box>
        </Stack>
      </Paper>
    </Modal>
  );
};

UserFormModal.defaultProps = {
  user: null,
};

export default UserFormModal;
